import axios from "axios";
import { XMLParser, XMLBuilder } from "fast-xml-parser";
import axiosConfig from "@/config/axios";
import config from "@/config/indo";
import { useCnxtSessionStore } from "@/stores/cnxtSession";

const SESSION_TOKEN_HEADER = "Session-Token";

/** Evita `//ruta` cuando VITE_APP_CNXT_API termina en `/`. */
function joinCnxtPath(baseUrl, path) {
  const base = String(baseUrl || "")
    .trim()
    .replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return `${base}/${p}`;
}

/**
 * GET contra la API CNXT/Mimetika. Envía siempre el `Session-Token` guardado y
 * guarda el que devuelva la API (axios normaliza las cabeceras a minúsculas).
 */
async function cnxtGet(path, options = {}) {
  const session = useCnxtSessionStore();
  const token = session.cnxtMimetikaSessionToken;
  const response = await axios.get(joinCnxtPath(axiosConfig.cnxtApiURL, path), {
    ...options,
    headers: {
      ...axiosConfig.headersOfertas,
      ...(token ? { [SESSION_TOKEN_HEADER]: token } : {}),
    },
  });
  const receivedToken = response?.headers?.[SESSION_TOKEN_HEADER.toLowerCase()];
  if (receivedToken) session.setSessionToken(receivedToken);
  return response;
}

/** Sin sesión, las llamadas iniciales devuelven la URL de login en lugar de datos. */
export function isCnxtLoginUrl(data) {
  return typeof data === "string" && data.trim().startsWith("https");
}

/** GET de una llamada inicial (GetPacientes / Mimetika_Measurements): actualiza el estado de conexión. */
async function cnxtInitialGet(path) {
  const response = await cnxtGet(path);
  useCnxtSessionStore().setConnected(!isCnxtLoginUrl(response?.data));
  return response;
}

/**
 * Busca un fragmento XML embebido en JSON (claves habituales y un nivel de anidación).
 */
function findEmbeddedXmlString(obj, depth = 0) {
  if (depth > 4 || obj == null) return "";
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = findEmbeddedXmlString(item, depth + 1);
      if (found) return found;
    }
    return "";
  }
  if (typeof obj !== "object") return "";
  const keys = ["XML", "xml", "Xml", "RawXml", "rawXml", "Data", "data", "content", "Content"];
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string" && (v.includes("<") || v.length > 20)) return v;
  }
  for (const key of ["result", "data", "value", "payload", "GetB2BOpticsResult"]) {
    const inner = obj[key];
    if (inner && typeof inner === "object") {
      const found = findEmbeddedXmlString(inner, depth + 1);
      if (found) return found;
    }
  }
  return "";
}

/** BOM delante de `<?xml` impide `startsWith('<')` si no se quita antes. */
function stripLeadingBom(s) {
  return typeof s === "string" ? s.replace(/^\uFEFF/, "") : "";
}

export default {
  getPacientes() {
    return cnxtInitialGet("GetPacientes");
  },

  getLecturasMID() {
    return cnxtInitialGet("Mimetika_Measurements");
  },

  getSesiones(patientId) {
    return cnxtGet(`GetSesiones?idsesion=${encodeURIComponent(patientId)}`);
  },

  async getB2BOptics(sessionId) {
    const response = await cnxtGet(`GetB2BOptics?idsesion=${encodeURIComponent(sessionId)}`, {
      // Cuerpo crudo: si el servidor manda XML, no lo parsea axios a objeto ([object Object] en CDATA).
      responseType: "text",
    });

    const raw = response.data == null ? "" : String(response.data);
    const body = stripLeadingBom(raw);
    const trimmed = body.trimStart();
    // Preservar atributos: el XML b2bOptic identifica el ojo con
    // `<lens side="RIGHT|LEFT">`. Con la config por defecto de fast-xml-parser
    // los atributos se descartan y los dos lentes quedan indistinguibles,
    // lo que provoca cruce OD/OI aguas abajo.
    const parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: "@_",
    });

    let jsonResult = {};
    let xmlString = "";

    if (!trimmed) {
      return { data: {}, xml: "" };
    }

    // Respuesta real GetB2BOptics: XML `b2bOptic` (p. ej. schema b2boptic_lensorder_v1.6.3.xsd)
    if (trimmed.startsWith("<")) {
      xmlString = body;
      try {
        jsonResult = parser.parse(body);
      } catch {
        jsonResult = {};
      }
    } else if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        jsonResult = JSON.parse(body);
      } catch {
        jsonResult = {};
        xmlString = body;
      }
      if (jsonResult && typeof jsonResult === "object") {
        xmlString = findEmbeddedXmlString(jsonResult);
        if (!xmlString) {
          try {
            const builder = new XMLBuilder({
              format: true,
              ignoreAttributes: false,
              attributeNamePrefix: "@_",
            });
            xmlString = builder.build(jsonResult);
          } catch (e) {
            xmlString = "";
          }
        }
      } else if (typeof jsonResult === "string") {
        xmlString = jsonResult;
      }
    } else {
      xmlString = body;
      try {
        jsonResult = parser.parse(body);
      } catch {
        jsonResult = {};
      }
    }


    return {
      data: jsonResult,
      xml: xmlString,
    };
  },
  getSingleMeasure(uuid) {
    return cnxtGet(`Mimetika_MeasurementDetail?uuid=${encodeURIComponent(uuid)}`);
  },

  saveMimetikaData(username, token, profile, mId, refPedido) {
    const payload = config.guardarDatosMimetika.xml
      .replace("{cliente}", username)
      .replace("{ref_pedido}", refPedido || mId.Referencia)
      .replace("{mimetika_id}", mId.processed_data?.measurement_uuid || mId.uuid)
      .replace("{mimetika_fecha}", mId.Fecha)
      .replace("{datos_json}", JSON.stringify(mId.datos));

    return axios.post(axiosConfig.indonetURL, payload, {
      headers: {
        ...axiosConfig.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  },
};
